import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { signIn, getUserRole, resetPassword } from '../firebase/auth';
import { motion } from 'framer-motion';
import { GiCupcake, GiKnifeFork } from 'react-icons/gi';
import { FiLogIn, FiX, FiMail } from 'react-icons/fi';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();
  
  // Reset password state
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetError, setResetError] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState('');

  async function handleSubmit(e) {
    e.preventDefault();
    
    try {
      setError('');
      setSuccessMessage('');
      setLoading(true);
      
      // First use our enhanced signIn function for better error handling
      const result = await signIn(email, password);
      
      if (!result.success) {
        setError(result.message);
        setLoading(false);
        return;
      }
      
      // If successful, also update the auth context
      await login(email, password);
      
      // Check if user is admin and redirect to appropriate dashboard
      const userRole = await getUserRole(result.user.uid);
      const redirectPath = userRole === 'admin' ? '/admin' : '/dashboard';
      
      setSuccessMessage(`Login successful! Redirecting to ${userRole} dashboard...`);
      
      // Navigate after a brief delay to show the success message
      setTimeout(() => {
        navigate(redirectPath);
      }, 1000);
    } catch (error) {
      let errorMessage = 'Failed to log in.';
      
      if (error.code === 'auth/user-not-found') {
        errorMessage = 'User not found. Please check your email or sign up.';
      } else if (error.code === 'auth/wrong-password') {
        errorMessage = 'Incorrect password. Please try again.';
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = 'Too many failed login attempts. Try again later or reset your password.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
      console.error(error);
      setLoading(false);
    }
  }
  
  // Handle opening the reset password modal
  const handleOpenResetModal = (e) => {
    e.preventDefault();
    setResetEmail(email); // Pre-fill with login email if available
    setResetError('');
    setResetSuccess('');
    setShowResetModal(true);
  };
  
  // Handle closing the reset password modal
  const handleCloseResetModal = () => {
    setShowResetModal(false);
    setResetEmail('');
    setResetError('');
    setResetSuccess('');
  };
  
  // Handle the password reset request
  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (!resetEmail.trim()) {
      setResetError('Please enter your email address');
      return;
    }
    
    try {
      setResetLoading(true);
      setResetError('');
      setResetSuccess('');
      
      const result = await resetPassword(resetEmail);
      
      if (result.success) {
        setResetSuccess(result.message);
        // Close the modal after a delay
        setTimeout(() => {
          handleCloseResetModal();
        }, 3000);
      } else {
        setResetError(result.message);
      }
    } catch (error) {
      setResetError('Failed to send password reset email');
      console.error(error);
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className="min-h-screen food-pattern-bg flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="flex justify-center mb-4">
        <motion.div
          animate={{ rotate: [0, 10, -10, 0] }}
          transition={{ duration: 0.5, repeat: 2, repeatDelay: 3 }}
        >
          <GiCupcake className="text-5xl" style={{ color: 'var(--accent)' }} />
        </motion.div>
      </div>
      
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h1 className="text-center font-display text-secondary" style={{ textShadow: '2px 2px 0px rgba(231, 76, 60, 0.15)' }}>
          Welcome Back!
        </h1>
        {/* <p className="mt-2 text-center" style={{ color: 'var(--text-secondary)' }}>
          Or{' '}
          <Link to="/signup" className="font-medium" style={{ color: 'var(--accent)' }}>
            create a new account
          </Link>
        </p> */}
      </div>

      <div className=" sm:mx-auto sm:w-full sm:max-w-md">
        <div className="card-funky py-8 px-6">
          <div className="flex justify-center mb-6">
            <div className="food-icon icon-pizza"></div>
            <div className="food-icon icon-burger"></div>
            <div className="food-icon icon-ice-cream"></div>
          </div>
          
          {error && (
            <motion.div 
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-4 p-3 rounded-lg"
              style={{ backgroundColor: 'rgba(231, 76, 60, 0.1)', border: '1px solid var(--error)', color: 'var(--error)' }}
            >
              <span className="block sm:inline">{error}</span>
            </motion.div>
          )}
          
          {successMessage && (
            <motion.div 
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-4 p-3 rounded-lg"
              style={{ backgroundColor: 'rgba(39, 174, 96, 0.1)', border: '1px solid var(--success)', color: 'var(--success)' }}
            >
              <span className="block sm:inline">{successMessage}</span>
            </motion.div>
          )}
          
          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="email" className="block text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
                Email address
              </label>
              <div className="mt-1">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  style={{ borderRadius: 'var(--radius-md)' }}
                  className="appearance-none block w-full p-3 border placeholder-gray-400 focus:outline-none focus:ring-2 sm:text-sm"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
                Password
              </label>
              <div className="mt-1">
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  style={{ borderRadius: 'var(--radius-md)' }}
                  className="appearance-none block w-full p-3 border placeholder-gray-400 focus:outline-none focus:ring-2 sm:text-sm"
                />
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300"
                  style={{ accentColor: 'var(--primary)' }}
                />
                <label htmlFor="remember-me" className="ml-2 block text-sm" style={{ color: 'var(--text-secondary)' }}>
                  Remember me
                </label>
              </div>

              <div className="text-sm">
                <button 
                  type="button"
                  onClick={handleOpenResetModal}
                  className="font-medium focus:outline-none focus:underline"
                  style={{ color: 'var(--secondary)' }}
                >
                  Forgot password?
                </button>
              </div>
            </div>

            <div>
              <motion.button
                type="submit"
                disabled={loading}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className={`w-full flex justify-center items-center py-3 px-4 border-0 rounded-full shadow-sm text-white text-sm font-medium ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}
                style={{ 
                  backgroundColor: 'var(--accent)',
                  boxShadow: 'var(--btn-shadow)'
                }}
              >
                {loading ? (
                  <>
                    <div className="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                    Signing in...
                  </>
                ) : (
                  <>
                    <FiLogIn className="mr-2" />
                    Sign in
                  </>
                )}
              </motion.button>
            </div>
          </form>

          <div className="mt-8 relative">
            <div className="divider-dots"></div>
            <div className="relative flex justify-center">
              <span className="px-4 text-sm" style={{ color: 'var(--text-secondary)' }}>
                New to HappyBubble?
              </span>
            </div>
          </div>

          <div className="mt-6">
            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <Link 
                to="/signup"
                className="w-full flex justify-center items-center py-3 px-4 border-2 rounded-full shadow-sm text-sm font-medium"
                style={{ 
                  borderColor: 'var(--accent)',
                  color: 'black'
                }}
              >
                <GiKnifeFork className="mr-2" />
                Create an account
              </Link>
            </motion.div>
          </div>
        </div>
        
        <div className="mt-6 text-center text-sm" style={{ color: 'var(--text-secondary)' }}>
          By signing in, you agree to our Terms of Service and Privacy Policy
        </div>
      </div>
      
      {/* Password Reset Modal */}
      {showResetModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="relative bg-white rounded-lg p-6 max-w-md w-full mx-4"
            style={{ borderRadius: 'var(--radius-lg)' }}
          >
            <button 
              onClick={handleCloseResetModal}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <FiX size={20} />
            </button>
            
            <div className="text-center mb-6">
              <FiMail className="mx-auto mb-2 text-3xl" style={{ color: 'var(--primary)' }} />
              <h3 className="text-xl font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>
                Reset Your Password
              </h3>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                Enter your email address and we'll send you a link to reset your password
              </p>
            </div>
            
            {resetError && (
              <div className="mb-4 p-3 rounded-lg text-sm" style={{ backgroundColor: 'rgba(231, 76, 60, 0.1)', border: '1px solid var(--error)', color: 'var(--error)' }}>
                {resetError}
              </div>
            )}
            
            {resetSuccess && (
              <div className="mb-4 p-3 rounded-lg text-sm" style={{ backgroundColor: 'rgba(39, 174, 96, 0.1)', border: '1px solid var(--success)', color: 'var(--success)' }}>
                {resetSuccess}
              </div>
            )}
            
            <form onSubmit={handleResetPassword}>
              <div className="mb-4">
                <label htmlFor="reset-email" className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
                  Email address
                </label>
                <input
                  id="reset-email"
                  type="email"
                  value={resetEmail}
                  onChange={e => setResetEmail(e.target.value)}
                  className="w-full p-3 border rounded-md"
                  style={{ borderRadius: 'var(--radius-md)' }}
                  required
                />
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={handleCloseResetModal}
                  className="px-4 py-2 border rounded-md text-sm"
                  style={{ 
                    borderRadius: 'var(--radius-md)',
                    color: 'var(--text-secondary)',
                    borderColor: 'var(--border-color)'
                  }}
                >
                  Cancel
                </button>
                <motion.button
                  type="submit"
                  disabled={resetLoading}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className={`px-4 py-2 rounded-md text-sm text-white ${resetLoading ? 'opacity-70 cursor-not-allowed' : ''}`}
                  style={{ 
                    borderRadius: 'var(--radius-md)',
                    backgroundColor: 'var(--primary)'
                  }}
                >
                  {resetLoading ? (
                    <>
                      <div className="inline-block animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                      Sending...
                    </>
                  ) : (
                    'Send Reset Link'
                  )}
                </motion.button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
} 